import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileText } from "lucide-react";
import { IMAGES, SERVICES } from "@/data/site";
import { PRODUCTS } from "@/data/products";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Pillars from "@/components/Pillars";
import CTASection from "@/components/CTASection";
import ProductCard from "@/components/ProductCard";
import { Eyebrow, usePageTitle } from "@/components/PageHeader";
import PoligoniSection from "@/components/PoligoniSection";
import { pageSeo } from "@/lib/seo";

const Hero = () => (
  <section data-testid="home-hero" className="relative flex min-h-[100svh] items-end overflow-hidden">
    <img src={IMAGES.electrical} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
    <div className="relative mx-auto w-full max-w-[1600px] px-6 pb-20 pt-32 md:px-12 md:pb-28">
      <Reveal>
        <Eyebrow>Magit di Gianfrancesco Maurizio S.r.l.</Eyebrow>
        <p className="font-display font-extrabold uppercase leading-[0.88] tracking-tight text-white text-[clamp(3.5rem,12vw,10.5rem)]">
          Impiantistica<span className="text-brand">.</span>
          <br />
          <span className="text-outline">Clima.</span>
          <br />
          Precisione<span className="text-brand">.</span>
        </p>
      </Reveal>
      <Reveal delay={200} className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <div className="max-w-xl">
          <h1 data-testid="home-h1" className="text-lg font-semibold leading-snug text-white md:text-xl">
            {pageSeo("/").h1}
          </h1>
          <p className="mt-3 text-base leading-relaxed text-white/70 md:text-lg">
            Dal 2009 impianti elettrici, manutenzione tecnologica e manutenzione specialistica di poligoni di tiro in
            tutta Italia, eseguiti a regola d'arte.
          </p>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <Link
            to="/servizi"
            data-testid="hero-services-button"
            className="group inline-flex items-center gap-3 rounded-md bg-brand px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-hover"
          >
            I nostri servizi
            <ArrowRight className="h-[18px] w-[18px] transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/shop"
            data-testid="hero-shop-button"
            className="inline-flex items-center gap-3 rounded-md border border-white/20 px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:border-white/50"
          >
            <FileText className="h-4 w-4" /> Catalogo SHOP
          </Link>
        </div>
      </Reveal>
      <p className="mt-14 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/40">
        Napoli — Italia / Est. 2009
      </p>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="home-services" className="border-t border-white/10 bg-surface">
    <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <Eyebrow>Cosa facciamo</Eyebrow>
          <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white md:text-7xl">
            Tre specializzazioni, <span className="text-white/30">un unico standard.</span>
          </h2>
        </Reveal>
        <Link to="/servizi" data-testid="home-all-services-link" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand hover:text-brand-hover">
          Tutti i servizi <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 120}>
            <Link to={s.link || "/servizi"} data-testid={`home-service-${s.id}`} className="group block overflow-hidden rounded-xl border border-white/10 bg-ink transition-colors hover:border-brand/40">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7">
                <span className="font-display text-sm font-bold text-brand">{s.n}</span>
                <h3 className="mt-3 font-display text-2xl font-bold uppercase text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{s.short}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/70 group-hover:text-brand">
                  Scopri di più <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ShopPreview = () => (
  <section data-testid="home-shop-preview" className="border-t border-white/10 bg-ink">
    <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <Eyebrow>Shop / Catalogo</Eyebrow>
          <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-white md:text-7xl">
            Materiali per <span className="text-white/30">poligoni di tiro.</span>
          </h2>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/55 md:text-base">
            Seleziona uno o più articoli e richiedi un preventivo: nessun pagamento online, ti ricontattiamo noi.
          </p>
        </Reveal>
        <Link to="/shop" data-testid="home-shop-link" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand hover:text-brand-hover">
          Vai allo SHOP <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {PRODUCTS.slice(0, 3).map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  usePageTitle("/");
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Pillars />
      <PoligoniSection />
      <ServicesPreview />
      <ShopPreview />
      <CTASection />
    </main>
  );
}
